import { CSSProperties, ReactElement, ReactNode, useState } from 'react';
import { NavLink } from 'react-router-dom';
import { useNavigateWithUTMs } from '../../../hooks/useNavigateWithUTMs';
import { useQueryClient } from '@tanstack/react-query';
import { parseZone } from 'moment';
import { useMenuWithCart } from '../../../contexts/MenuWithCart';
import { AlertModal, AlertModalVariant } from '../modal/AlertModal';
import { Menu } from '../../../models/menu.types';
import { clearCache } from '../../../services/user/user.queries';
import { useNotification } from '../../../contexts/NotificationContext';
import { Toast, ToastVariant } from '../notification/Toast';

type Props = {
  href: string;
  className?: string | ((props: { isActive: boolean }) => string);
  dataHeapId?: string;
  style?: CSSProperties;
  children: ReactNode;
  reload?: boolean;
  onClick?: () => void;
  onModalClose?: () => void;
};

function Link({
  href,
  className = 'c-nav-header__link',
  children,
  style,
  dataHeapId,
  reload = false,
  onClick,
  onModalClose,
}: Props): ReactElement {
  const { navigate, appendUTMs } = useNavigateWithUTMs();
  const { showNotification } = useNotification();
  const {
    cart,
    isCartChanged,
    setIsCartChanged,
    upcomingMenus,
    menu: currentMenu,
  } = useMenuWithCart();
  const queryClient = useQueryClient();
  const cartIds = upcomingMenus
    ?.menus.filter((menu: Menu) => menu?.cartId !== null)
    .map((menu: Menu) => menu?.cartId) || [];

  const currentMenuDateString: string | undefined = currentMenu?.deliveryOn
    ? parseZone(currentMenu?.deliveryOn)
      .format('MMMM D') : '';

  const [modalVariant, setModalVariant] = useState<AlertModalVariant | null>(null);

  const verifyCart = (e: React.MouseEvent) => {
    const isLeavingCart = !(href.endsWith('/upcoming-menus'));

    if (!isLeavingCart) {
      // if we're navigating to upcoming-menus, clear the Tanstack cache to reflect any data changes
      // that may have occurred on the legacy pages
      clearCache(queryClient, cartIds, currentMenu?.id);
    } else if (isLeavingCart && cart?.priceSummary?.remainingMinimumAmount) {
      e.preventDefault();
      setModalVariant(AlertModalVariant.CART_BELOW_MINIMUM);
      return;
    }
    if (isLeavingCart && isCartChanged && cart) {
      setIsCartChanged(false);
      showNotification({
        content: <Toast variant={ToastVariant.CART_SAVED} date={currentMenuDateString} />,
        duration: 5000,
      });
    }

    onClick?.();
  };

  const onModalClick = () => {
    if (reload) {
      window.location.href = href;
    }
    navigate(href);
    showNotification({
      content: <Toast variant={ToastVariant.CART_SAVED} date={currentMenuDateString} />,
      duration: 5000,
    });
  };

  return (
    <>
      {reload ? (
        <a
          className={
            typeof className === 'function' ? className({ isActive: false }) : className
          }
          data-heap-id={dataHeapId}
          href={href}
          onClick={verifyCart}
        >
          {children}
        </a>
      ) : (
        <NavLink
          style={style}
          className={className}
          data-heap-id={dataHeapId}
          onClick={verifyCart}
          to={appendUTMs(href)}
        >
          {children}
        </NavLink>
      )}

      {!!modalVariant && (
        <AlertModal
          variant={modalVariant}
          isOpen
          onClose={() => {
            setModalVariant(null);
            onModalClose?.();
          }}
          onClick={onModalClick}
        />
      )}
    </>
  );
}

export default Link;
